import Icon from 'embed_v2/components/elements/common/Icon'
import useLightenBackground from 'embed_v2/hooks/useLightenBackground'
import Version, { HandlerIcon } from 'embed_v2/types/version'
import { CSSProperties, memo } from 'preact/compat'
import { widthToValue } from 'core/helpers/styles'
import type ItemApi from '../../ItemApi'

const iconOptions = (icon: HandlerIcon) => ({
  color: `rgba(${icon.color})`,
  fontSize: icon.size
})

type Props = {
  api: ItemApi
  options: Version['options']['handler']
}

const ModalHandler = (props: Props) => {
  const { options, api } = props
  const { left_icon: leftIcon, right_icon: rightIcon } = options
  const node = useLightenBackground<HTMLButtonElement>()

  const handlerStyles: CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    wordWrap: 'break-word',
    whiteSpace: 'pre',
    cursor: 'pointer',
    boxSizing: 'border-box',
    maxWidth: '100%',
    width: widthToValue(options.width, options.width_fixed, options.width_percent),
    height: 'auto',
    minHeight: `${options.height}px`,
    marginLeft: options.align === 'left' ? '0' : 'auto',
    marginRight: options.align === 'right' ? '0' : 'auto',
    paddingTop: `${options.padding_top}px`,
    paddingRight: `${options.padding_right}px`,
    paddingBottom: `${options.padding_bottom}px`,
    paddingLeft: `${options.padding_left}px`,
    backgroundColor: `rgba(${options.bg_color})`,
    borderTop: options.border_side[0]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderRight: options.border_side[1]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderBottom: options.border_side[2]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderLeft: options.border_side[3]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderTopLeftRadius: `${options.border_top_left}px`,
    borderTopRightRadius: `${options.border_top_right}px`,
    borderBottomRightRadius: `${options.border_bottom_left}px`,
    borderBottomLeftRadius: `${options.border_bottom_right}px`
  }

  const textStyles: CSSProperties = {
    width: '100%',
    padding: '0 10px',
    wordWrap: 'break-word',
    whiteSpace: 'normal',
    color: `rgba(${options.text.text_color})`,
    lineHeight: options.text.line_height,
    fontFamily: options.text.font_family,
    fontSize: `${options.text.font_size}px`,
    fontWeight: options.text.font_weight,
    textAlign: options.text.text_align,
    textDecoration: options.text.text_decoration,
    textShadow: options.text.text_shadow,
    textTransform: options.text.text_transform,
    letterSpacing: options.text.letter_spacing
  }

  const subtextStyles: CSSProperties = {
    width: '100%',
    wordWrap: 'break-word',
    whiteSpace: 'normal',
    marginTop: `${options.subtext.margin_top}px`,
    color: `rgba(${options.subtext.text_color})`,
    lineHeight: options.subtext.line_height,
    fontFamily: options.subtext.font_family,
    fontSize: `${options.subtext.font_size}px`,
    fontWeight: options.subtext.font_weight,
    textAlign: options.subtext.text_align,
    textDecoration: options.subtext.text_decoration,
    textShadow: options.subtext.text_shadow,
    textTransform: options.subtext.text_transform,
    letterSpacing: options.subtext.letter_spacing
  }

  const textWrapperStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }

  const onClick = () => {
    api.show()
  }

  return (
    <button className="wf_reset wf_modal wf_handler" ref={node} onClick={onClick} style={handlerStyles}>
      <div className="wf_reset wf_handler_text_wrapper" style={textWrapperStyles}>
        {leftIcon && (
          <Icon className="wf_reset wf_handler wf_left_icon" code={leftIcon.code} styles={iconOptions(leftIcon)} />
        )}
        <div className="wf_reset wf_handler_text" style={textStyles}>
          {options.button_text}
        </div>
        {rightIcon && (
          <Icon className="wf_reset wf_handler wf_right_icon" code={rightIcon.code} styles={iconOptions(rightIcon)} />
        )}
      </div>
      {!!options.button_subtext?.length && (
        <div className="wf_reset wf_handler_subtext" style={subtextStyles}>
          {options.button_subtext}
        </div>
      )}
    </button>
  )
}

export default memo(ModalHandler)
